import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  ArrowUpDown,
  ChevronRight,
  GripVertical,
  Layers,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import { useUserProfile } from '../../context/UserProfileContext';
import { useOrgServices, type OrgServiceItemInput } from '../../hooks/useOrgServices';
import { ConfirmModal } from '../../components/admin/ConfirmModal';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { OrgServiceItem, QuoteType } from '../../types/admin';

// ── Constants ──────────────────────────────────────────────────────────────

const PARTY_OPTIONS = ['Customer', 'Mover'] as const;
type Party = (typeof PARTY_OPTIONS)[number];

// ── Field label ────────────────────────────────────────────────────────────

function FieldLabel({
  htmlFor,
  children,
  optional,
}: {
  htmlFor: string;
  children: React.ReactNode;
  optional?: boolean;
}) {
  return (
    <label
      htmlFor={htmlFor}
      className="block text-[11px] font-bold uppercase tracking-widest text-slate"
    >
      {children}
      {!optional && <span className="text-rose ml-0.5">*</span>}
      {optional && (
        <span className="text-slate font-normal normal-case tracking-normal ml-1">(optional)</span>
      )}
    </label>
  );
}

// ── Service Form (bottom sheet) ────────────────────────────────────────────

interface ServiceFormProps {
  initial?: OrgServiceItem;
  onClose: () => void;
  onSave: (input: OrgServiceItemInput) => Promise<unknown>;
}

function ServiceForm({ initial, onClose, onSave }: ServiceFormProps) {
  const [name, setName] = useState(initial?.name ?? '');
  const [code, setCode] = useState(initial?.code ?? '');
  const [party, setParty] = useState<Party>(initial?.party ?? 'Customer');
  const [quoteType, setQuoteType] = useState<QuoteType>(initial?.quoteType ?? 'commercial');
  const [note, setNote] = useState(initial?.note ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) { setError('Service name is required.'); return; }
    setError('');
    setSaving(true);
    try {
      await onSave({
        name: name.trim(),
        code: code.trim() || undefined,
        party,
        note: note.trim() || undefined,
        quoteType,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save service.');
      setSaving(false);
    }
  }

  return (
    /* Mobile: bottom sheet. md+: centered dialog. */
    <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
      <div className="absolute inset-0 bg-midnight/50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-t-2xl px-4 pt-5 pb-10 max-h-[92vh] overflow-y-auto
                      md:rounded-2xl md:max-w-md md:w-full md:max-h-[80vh] md:pb-6">
        {/* Drag handle — mobile only */}
        <div className="md:hidden w-10 h-1 bg-stone rounded-full mx-auto mb-5" />

        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-h3 text-midnight">
            {initial ? 'Edit Service' : 'Add Service'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate
                       hover:text-midnight hover:bg-mist transition-colors"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {error && (
            <div className="flex items-start gap-2 px-4 py-3 rounded-lg bg-rose/10 border border-rose/30">
              <AlertCircle size={15} className="shrink-0 text-rose mt-0.5" />
              <p className="text-small text-rose">{error}</p>
            </div>
          )}

          {/* Service name */}
          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="osvc-name">Service</FieldLabel>
            <input
              id="osvc-name"
              type="text"
              autoComplete="off"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Furniture Protection"
              className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                         text-small text-midnight placeholder:text-slate
                         focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                         transition-colors duration-150"
            />
          </div>

          {/* Code */}
          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="osvc-code" optional>Code</FieldLabel>
            <input
              id="osvc-code"
              type="text"
              autoComplete="off"
              value={code}
              onChange={e => setCode(e.target.value)}
              placeholder="e.g. A1 or MO2"
              className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                         text-small text-midnight placeholder:text-slate
                         focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                         transition-colors duration-150"
            />
          </div>

          {/* Party */}
          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="osvc-party">Party</FieldLabel>
            <div className="flex bg-mist rounded-lg p-1 gap-1">
              {PARTY_OPTIONS.map(p => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setParty(p)}
                  className={`flex-1 py-2.5 text-sm font-semibold rounded-md transition-all duration-150
                    ${party === p
                      ? 'bg-white text-midnight shadow-brand-sm'
                      : 'text-slate hover:text-midnight'
                    }`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          {/* Quote Type */}
          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="osvc-quotetype">Quote Type</FieldLabel>
            <div className="flex bg-mist rounded-lg p-1 gap-1">
              {(['commercial', 'residential'] as const).map(qt => (
                <button
                  key={qt}
                  type="button"
                  onClick={() => setQuoteType(qt)}
                  className={`flex-1 py-2.5 text-sm font-semibold rounded-md transition-all duration-150
                    ${quoteType === qt
                      ? 'bg-white text-midnight shadow-brand-sm'
                      : 'text-slate hover:text-midnight'
                    }`}
                >
                  {qt === 'commercial' ? 'Commercial' : 'Residential'}
                </button>
              ))}
            </div>
          </div>

          {/* Note */}
          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="osvc-note" optional>Note</FieldLabel>
            <textarea
              id="osvc-note"
              rows={2}
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Optional context for estimators…"
              className="w-full px-3 py-2.5 rounded-lg border border-stone bg-white
                         text-small text-midnight placeholder:text-slate resize-none
                         focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                         transition-colors duration-150"
            />
          </div>

          <button
            type="submit"
            disabled={saving}
            className="h-11 rounded-lg bg-plum text-white text-[13px] font-semibold
                       flex items-center justify-center gap-2
                       hover:bg-rose active:opacity-80 disabled:opacity-50
                       transition-colors duration-150 mt-1"
          >
            {saving ? 'Saving…' : initial ? 'Save Changes' : 'Add Service'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Party Badge ────────────────────────────────────────────────────────────

function PartyBadge({ party }: { party: Party }) {
  return (
    <span
      className={`shrink-0 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md
        ${party === 'Customer' ? 'bg-plum/10 text-plum' : 'bg-ember/10 text-ember'}`}
    >
      {party}
    </span>
  );
}

// ── Service Card ───────────────────────────────────────────────────────────

interface ServiceCardProps {
  service: OrgServiceItem;
  editingOrder: boolean;
  isDragOver: boolean;
  isDragging: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onDragStart: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onDragEnd: () => void;
}

function ServiceCard({
  service,
  editingOrder,
  isDragOver,
  isDragging,
  onEdit,
  onDelete,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}: ServiceCardProps) {
  return (
    <div
      draggable={editingOrder}
      onDragStart={editingOrder ? onDragStart : undefined}
      onDragOver={editingOrder ? onDragOver : undefined}
      onDrop={editingOrder ? onDrop : undefined}
      onDragEnd={editingOrder ? onDragEnd : undefined}
      className={`bg-white border rounded-xl p-4 shadow-brand-sm select-none
        transition-all duration-100
        ${isDragOver ? 'border-plum border-2' : 'border-stone'}
        ${isDragging ? 'opacity-40' : 'opacity-100'}`}
    >
      <div className="flex items-center gap-3">
        {editingOrder && (
          <div className="shrink-0 text-slate hover:text-midnight cursor-grab active:cursor-grabbing">
            <GripVertical size={18} />
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-small text-midnight leading-snug">
              {service.code && <span className="text-plum font-bold mr-1.5">{service.code}</span>}
              {service.name}
            </p>
            <PartyBadge party={service.party} />
          </div>
          {service.note && (
            <p className="text-[13px] text-slate mt-1 leading-snug line-clamp-2">{service.note}</p>
          )}
        </div>

        {!editingOrder && (
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={onEdit}
              className="h-8 w-8 flex items-center justify-center rounded-lg border border-stone
                         text-slate hover:border-plum hover:text-plum active:bg-mist
                         transition-colors duration-150"
              aria-label={`Edit ${service.name}`}
            >
              <Pencil size={14} />
            </button>
            <button
              type="button"
              onClick={onDelete}
              className="h-8 w-8 flex items-center justify-center rounded-lg border border-stone
                         text-slate hover:border-rose hover:text-rose active:bg-mist
                         transition-colors duration-150"
              aria-label={`Delete ${service.name}`}
            >
              <Trash2 size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Party Section ──────────────────────────────────────────────────────────

interface PartySectionProps {
  party: Party;
  services: OrgServiceItem[];
  editingOrder: boolean;
  draggedId: string | null;
  dragOverId: string | null;
  onEdit: (s: OrgServiceItem) => void;
  onDelete: (s: OrgServiceItem) => void;
  onDragStart: (e: React.DragEvent, id: string) => void;
  onDragOver: (e: React.DragEvent, id: string) => void;
  onDrop: (e: React.DragEvent, id: string) => void;
  onDragEnd: () => void;
}

function PartySection({
  party,
  services,
  editingOrder,
  draggedId,
  dragOverId,
  onEdit,
  onDelete,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}: PartySectionProps) {
  if (services.length === 0) return null;

  const dotColor = party === 'Customer' ? 'bg-plum' : 'bg-ember';

  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-2 flex items-center gap-1.5">
        <span className={`inline-block w-2 h-2 rounded-full ${dotColor}`} />
        {party}
        <span className="font-normal normal-case tracking-normal">({services.length})</span>
      </p>
      <ul className="flex flex-col gap-2">
        {services.map(service => (
          <li key={service.id}>
            <ServiceCard
              service={service}
              editingOrder={editingOrder}
              isDragOver={dragOverId === service.id}
              isDragging={draggedId === service.id}
              onEdit={() => onEdit(service)}
              onDelete={() => onDelete(service)}
              onDragStart={e => onDragStart(e, service.id)}
              onDragOver={e => onDragOver(e, service.id)}
              onDrop={e => onDrop(e, service.id)}
              onDragEnd={onDragEnd}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Inner Content ──────────────────────────────────────────────────────────

function OrgServicesContent({ orgId }: { orgId: string }) {
  const navigate = useNavigate();
  const { adminOrg } = useUserProfile();
  const { items, loading, error, createItem, updateItem, deleteItem, reorderItems, syncFromMaster } =
    useOrgServices(orgId);

  const commercialEnabled = adminOrg?.featureCommercialMovingQuotes !== false;
  const residentialEnabled = adminOrg?.featureResidentialMovingQuotes ?? false;
  const bothEnabled = commercialEnabled && residentialEnabled;
  const defaultView: QuoteType = !commercialEnabled && residentialEnabled ? 'residential' : 'commercial';

  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState<QuoteType>(defaultView);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItem, setEditingItem] = useState<OrgServiceItem | null>(null);
  const [deletingItem, setDeletingItem] = useState<OrgServiceItem | null>(null);
  const [confirmRestore, setConfirmRestore] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<{ added: number; updated: number } | null>(null);
  const [syncError, setSyncError] = useState('');

  // Edit-order mode
  const [editingOrder, setEditingOrder] = useState(false);
  const [orderedItems, setOrderedItems] = useState<OrgServiceItem[]>([]);
  const [savingOrder, setSavingOrder] = useState(false);
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  function enterEditOrder() {
    setOrderedItems([...items]);
    setEditingOrder(true);
  }

  function cancelEditOrder() {
    setEditingOrder(false);
    setOrderedItems([]);
    setDraggedId(null);
    setDragOverId(null);
  }

  async function saveOrder() {
    setSavingOrder(true);
    try {
      await reorderItems(orderedItems.map(s => s.id));
      setEditingOrder(false);
      setOrderedItems([]);
    } finally {
      setSavingOrder(false);
    }
  }

  function handleDragStart(e: React.DragEvent, id: string) {
    e.dataTransfer.setData('text/plain', id);
    e.dataTransfer.effectAllowed = 'move';
    setDraggedId(id);
  }

  function handleDragOver(e: React.DragEvent, id: string) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (id !== draggedId) setDragOverId(id);
  }

  function handleDrop(e: React.DragEvent, targetId: string) {
    e.preventDefault();
    const sourceId = draggedId ?? e.dataTransfer.getData('text/plain');
    if (!sourceId || sourceId === targetId) {
      setDraggedId(null);
      setDragOverId(null);
      return;
    }
    setOrderedItems(prev => {
      const source = prev.find(s => s.id === sourceId);
      const target = prev.find(s => s.id === targetId);
      // Reordering only within the same party group
      if (!source || !target || source.party !== target.party) return prev;
      const result = [...prev];
      const srcIdx = result.findIndex(s => s.id === sourceId);
      const tgtIdx = result.findIndex(s => s.id === targetId);
      result.splice(srcIdx, 1);
      result.splice(tgtIdx, 0, source);
      return result;
    });
    setDraggedId(null);
    setDragOverId(null);
  }

  function handleDragEnd() {
    setDraggedId(null);
    setDragOverId(null);
  }

  async function handleSync() {
    setSyncing(true);
    setSyncError('');
    setSyncResult(null);
    try {
      const result = await syncFromMaster();
      setSyncResult(result);
    } catch (err) {
      setSyncError(err instanceof Error ? err.message : 'Restore failed.');
    } finally {
      setSyncing(false);
    }
  }

  // Filter by view mode first
  const viewItems = items.filter(s =>
    viewMode === 'commercial'
      ? (s.quoteType ?? 'commercial') === 'commercial'
      : s.quoteType === 'residential',
  );

  const displayItems = editingOrder ? orderedItems : viewItems;
  const filtered = editingOrder
    ? orderedItems.filter(s =>
        viewMode === 'commercial'
          ? (s.quoteType ?? 'commercial') === 'commercial'
          : s.quoteType === 'residential',
      )
    : viewItems.filter(
        s =>
          s.name.toLowerCase().includes(search.toLowerCase()) ||
          s.code?.toLowerCase().includes(search.toLowerCase()) ||
          s.party.toLowerCase().includes(search.toLowerCase()) ||
          s.note?.toLowerCase().includes(search.toLowerCase()),
      );

  const customerItems = filtered.filter(s => s.party === 'Customer');
  const moverItems = filtered.filter(s => s.party === 'Mover');

  return (
    <div className="px-4 pt-6 pb-8 md:px-8 md:max-w-5xl md:mx-auto">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1 text-small mb-4 -ml-1">
        <button
          type="button"
          onClick={() => navigate('/admin')}
          className="text-slate hover:text-midnight transition-colors duration-150"
        >
          Admin Panel
        </button>
        <ChevronRight size={14} className="text-stone" />
        <button
          type="button"
          onClick={() => navigate('/admin/quoting')}
          className="text-slate hover:text-midnight transition-colors duration-150"
        >
          Quoting
        </button>
        <ChevronRight size={14} className="text-stone" />
        <span className="text-midnight font-medium">Services</span>
      </nav>

      <h1 className="font-display text-h1 text-midnight mb-1">Services</h1>
      <p className="text-[13px] text-slate mb-5 leading-snug">
        Your organization's list of services offered during a move.
      </p>

      {/* Sync result / error banners */}
      {syncResult && (
        <div className="mb-4 flex items-start gap-3 px-4 py-3 rounded-xl bg-plum/8 border border-plum/20">
          <div className="flex-1 min-w-0">
            <p className="text-small font-semibold text-midnight">
              Restored from system list —{' '}
              {[
                syncResult.added > 0 && `${syncResult.added} added`,
                syncResult.updated > 0 && `${syncResult.updated} updated`,
              ]
                .filter(Boolean)
                .join(', ') || 'already up to date'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setSyncResult(null)}
            className="shrink-0 w-6 h-6 flex items-center justify-center text-slate hover:text-midnight"
            aria-label="Dismiss"
          >
            <X size={14} />
          </button>
        </div>
      )}
      {syncError && (
        <div className="mb-4 flex items-start gap-2 px-4 py-3 rounded-xl bg-rose/10 border border-rose/30">
          <AlertCircle size={15} className="shrink-0 text-rose mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-small text-rose">{syncError}</p>
          </div>
          <button
            type="button"
            onClick={() => setSyncError('')}
            className="shrink-0 w-6 h-6 flex items-center justify-center text-rose"
            aria-label="Dismiss"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Commercial / Residential toggle (only when both enabled) */}
      {bothEnabled && (
        <div className="flex bg-mist rounded-lg p-1 gap-1 mb-4">
          {(['commercial', 'residential'] as const).map(qt => (
            <button
              key={qt}
              type="button"
              onClick={() => { setViewMode(qt); setSearch(''); }}
              className={`flex-1 py-2.5 text-sm font-semibold rounded-md transition-all duration-150
                ${viewMode === qt
                  ? 'bg-white text-midnight shadow-brand-sm'
                  : 'text-slate hover:text-midnight'
                }`}
            >
              {qt === 'commercial' ? 'Commercial' : 'Residential'}
            </button>
          ))}
        </div>
      )}

      {/* Toolbar — normal mode */}
      {!editingOrder && (
        <div className="flex items-center gap-2 mb-5">
          <div className="relative flex-1">
            <Search
              size={15}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate pointer-events-none"
            />
            <input
              type="search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search services…"
              className="w-full h-11 pl-9 pr-3 rounded-xl border border-stone bg-white
                         text-small text-midnight placeholder:text-slate
                         focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                         transition-colors duration-150"
            />
          </div>

          {/* Edit order */}
          <button
            type="button"
            onClick={enterEditOrder}
            disabled={items.length < 2}
            className="shrink-0 h-11 w-11 rounded-xl border border-stone bg-white
                       flex items-center justify-center
                       text-slate hover:border-plum hover:text-plum active:bg-mist
                       disabled:opacity-40 disabled:cursor-not-allowed
                       transition-colors duration-150"
            aria-label="Edit order"
            title="Edit order"
          >
            <ArrowUpDown size={16} />
          </button>

          {/* Restore from system list */}
          <button
            type="button"
            onClick={() => setConfirmRestore(true)}
            disabled={syncing}
            className="shrink-0 h-11 px-3 rounded-xl border border-stone bg-white
                       flex items-center gap-1.5
                       text-[13px] font-semibold text-slate
                       hover:border-plum hover:text-plum active:bg-mist disabled:opacity-50
                       transition-colors duration-150"
            title="Restore from System List"
          >
            {syncing ? <LoadingSpinner size="sm" /> : <RefreshCw size={15} />}
            <span className="hidden sm:inline">Restore</span>
          </button>

          {/* Add service */}
          <button
            type="button"
            onClick={() => setShowAddForm(true)}
            className="shrink-0 h-11 w-11 rounded-xl bg-plum text-white
                       flex items-center justify-center
                       hover:bg-rose active:opacity-80
                       transition-colors duration-150"
            aria-label="Add service"
          >
            <Plus size={20} />
          </button>
        </div>
      )}

      {/* Toolbar — edit order mode */}
      {editingOrder && (
        <div className="flex items-center gap-2 mb-5 flex-wrap">
          <p className="flex-1 text-small text-slate">
            Drag <span className="font-semibold text-midnight">within</span> each group to reorder.
          </p>
          <button
            type="button"
            onClick={cancelEditOrder}
            disabled={savingOrder}
            className="h-11 px-4 rounded-xl border border-stone bg-white
                       text-small font-semibold text-slate
                       hover:border-rose hover:text-rose active:bg-mist
                       disabled:opacity-50
                       transition-colors duration-150"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={saveOrder}
            disabled={savingOrder}
            className="h-11 px-4 rounded-xl bg-plum text-white
                       text-small font-semibold
                       hover:bg-rose active:opacity-80 disabled:opacity-50
                       transition-colors duration-150"
          >
            {savingOrder ? 'Saving…' : 'Save Order'}
          </button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-16">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div className="flex flex-col items-center py-16 gap-2 text-center">
          <p className="text-small text-rose font-semibold">Failed to load services</p>
          <p className="text-small text-slate">{error}</p>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && viewItems.length === 0 && (
        <div className="flex flex-col items-center py-16 gap-3 text-center">
          <div className="w-14 h-14 rounded-full bg-mist flex items-center justify-center">
            <Layers size={24} className="text-slate" />
          </div>
          <p className="text-small text-slate">No {viewMode} services yet. Add one or restore from the system list.</p>
        </div>
      )}

      {/* No search results */}
      {!loading && !error && !editingOrder && viewItems.length > 0 && filtered.length === 0 && (
        <div className="flex flex-col items-center py-12 gap-2 text-center">
          <p className="text-small text-slate">No services match your search.</p>
        </div>
      )}

      {/* Service list */}
      {!loading && !error && (editingOrder ? displayItems.length > 0 : filtered.length > 0) && (
        <>
          {!editingOrder && (
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-3">
              {filtered.length} service{filtered.length !== 1 ? 's' : ''}
              {search && ` matching "${search}"`}
            </p>
          )}

          <div className="flex flex-col gap-5 md:flex-row md:gap-8 md:items-start">
            {customerItems.length > 0 && (
              <div className="md:flex-1 min-w-0">
                <PartySection
                  party="Customer"
                  services={customerItems}
                  editingOrder={editingOrder}
                  draggedId={draggedId}
                  dragOverId={dragOverId}
                  onEdit={setEditingItem}
                  onDelete={setDeletingItem}
                  onDragStart={handleDragStart}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  onDragEnd={handleDragEnd}
                />
              </div>
            )}
            {moverItems.length > 0 && (
              <div className="md:flex-1 min-w-0">
                <PartySection
                  party="Mover"
                  services={moverItems}
                  editingOrder={editingOrder}
                  draggedId={draggedId}
                  dragOverId={dragOverId}
                  onEdit={setEditingItem}
                  onDelete={setDeletingItem}
                  onDragStart={handleDragStart}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  onDragEnd={handleDragEnd}
                />
              </div>
            )}
          </div>
        </>
      )}

      {/* Add form */}
      {showAddForm && (
        <ServiceForm
          onClose={() => setShowAddForm(false)}
          onSave={createItem}
        />
      )}

      {/* Edit form */}
      {editingItem && (
        <ServiceForm
          initial={editingItem}
          onClose={() => setEditingItem(null)}
          onSave={input => updateItem(editingItem.id, input)}
        />
      )}

      {/* Delete confirm */}
      <ConfirmModal
        isOpen={!!deletingItem}
        title="Delete Service"
        message={`Are you sure you want to delete "${deletingItem?.name}"? This cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={() => {
          if (deletingItem) deleteItem(deletingItem.id);
          setDeletingItem(null);
        }}
        onClose={() => setDeletingItem(null)}
      />

      {/* Restore from system list confirm */}
      <ConfirmModal
        isOpen={confirmRestore}
        title="Restore from System List"
        message="This will update all system-sourced services with the latest values from the master services list, and add any new master services that aren't in your list yet. Services your organization added won't be affected."
        confirmLabel="Restore"
        variant="primary"
        onConfirm={handleSync}
        onClose={() => setConfirmRestore(false)}
      />
    </div>
  );
}

// ── Page (guards org admin access) ────────────────────────────────────────

export function OrgServicesPage() {
  const { adminOrg, loading } = useUserProfile();

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!adminOrg) {
    return (
      <div className="px-4 pt-6 flex flex-col items-center py-20 gap-3 text-center">
        <p className="text-small text-slate">You don't have org admin access.</p>
      </div>
    );
  }

  return <OrgServicesContent orgId={adminOrg.orgId} />;
}
